import { PropsWithChildren, useEffect, useState } from "react";
import { post } from "../../core/client";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { Dialog, DialogActions, DialogContent, DialogContentText, Grid, Skeleton } from "@mui/material";
import { IGridPagedModel } from "../../models/shared/GridModels";
import { DataTableCol } from "../../components/data-table/Models";
import DataTable from "../../components/data-table";
import dayjs from "dayjs";
import { CustomButton, CustomIconButton } from "../../components/buttons";
import { IsNullOrEmpty, IsNullOrZero } from "../../core/utility";
import { Cancel, CancelSharp } from "@mui/icons-material";

interface ListMvkReservationProps {
    isReport: boolean;
    memberId: number | null;
    beginDate?: string | null;
    endDate?: string | null;
    onDisabledMonthsChange?: (months: Set<string>) => void;
    onLoading?: (loading: boolean) => void;
    onCancel?: (cancelled: boolean) => void;
}

interface ListMvkReservationInfoRequest extends IGridPagedModel {
    memberId: number | null;
    beginDate: string | null;
    endDate: string | null;
}

interface ListMvkReservationInfoResponse {
    totalCount: number;
    items: Array<ListMvkReservationInfoResponseItem>;
}

interface ListMvkReservationInfoResponseItem {
    reservationId: number;
    memberId: number;
    firstName: string;
    lastName: string;
    company: string;
    reservationDate: string;
    phone: string;
}

interface CancelReservationRequest {
    reservationId: number;
}

interface CancelReservationResponse {
    success: boolean;
}

const initialReqState: ListMvkReservationInfoRequest = {
    pageNumber: 0,
    pageSize: 20,
    order: true,
    orderBy: 'reservationDate',
    memberId: null,
    beginDate: null,
    endDate: null,
};

const initialResState: ListMvkReservationInfoResponse = {
    totalCount: 0,
    items: [],
};


const ListMvkReservationComponent = (props: ListMvkReservationProps) => {

    const [requestState, setRequestState] = useState<ListMvkReservationInfoRequest>(initialReqState);
    const [responseState, setResponseState] = useState<ListMvkReservationInfoResponse>(initialResState);
    const [loading, setLoading] = useState(false);
    const [errorText, setErrorText] = useState<string>('');
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [selectedCancelReservationId, setSelectedCancelReservationId] = useState<number>(0);

    const dayjs = require('dayjs');
    var customParseFormat = require("dayjs/plugin/customParseFormat");
    dayjs.extend(customParseFormat);

    useEffect(() => {
        setRequestState(prevState => ({
            ...prevState,
            memberId: props.memberId,
            beginDate: props.beginDate ? props.beginDate : null,
            endDate: props.endDate ? props.endDate : null
        }));
    }, [props.memberId, props.beginDate, props.endDate]);

    useEffect(() => {
        (async () => {
            await handleListMemberReservationHistory(requestState);
        })();
    }, [requestState.memberId, requestState.beginDate, requestState.endDate]);

    useEffect(() => {
        if (props.onLoading) {
            props.onLoading(loading);
        }
    }, [loading]);

    const handleListMemberReservationHistory = async (request: ListMvkReservationInfoRequest) => {
        if (props.isReport || request.memberId !== null) {
            setLoading(true);
            const response = await post<ListMvkReservationInfoResponse>('api/external/list-mvk-reservation-info', request);
            if (response && response.items && response.items.length !== 0) {
                setErrorText("");
                setResponseState(prev => ({
                    ...prev,
                    totalCount: response.totalCount,
                    items: response.items
                }));
                
                handleSetDisabledMonths(response.items);
            } else if (response && response.items.length === 0) {
                setErrorText("Geçmiş rezervasyon bulunmamaktadır");
            } else {
                setErrorText("Sorgulama işlemi tamamlanamadı");
            }
            setLoading(false);
        } else {

        }
    };

    const handleCancelReservation = async (request: number) => {
        if (!props.isReport && !IsNullOrZero(request)) {
            setLoading(true);
            const reqObject: CancelReservationRequest = {reservationId: request};
            setSelectedCancelReservationId(0);
            setShowCancelDialog(false);
            const response = await post<CancelReservationResponse>('api/external/cancel-mvk-reservation', reqObject);
            if (response && response.success === true) {
                setErrorText("");
                if (props.onCancel) {
                    props.onCancel(true);
                }
            } else {
                setErrorText("Rezervasyon iptal işlemi başarısız oldu");
            }
            setLoading(false);
        } else {

        }
    };

    const handleSetDisabledMonths = (items: Array<ListMvkReservationInfoResponseItem>) => {
        const toYmKey = (d: dayjs.Dayjs) => d.format("MM.YYYY");
        const set = new Set<string>();
        for (const item of items) {
            const d = dayjs(item.reservationDate, "DD.MM.YYYY", false);
            if (d.isValid()) set.add(toYmKey(d));
        }
        if (props.onDisabledMonthsChange) {
            props.onDisabledMonthsChange(set);
        }
    };

    const clearResults = () => {
        setErrorText("");
        setResponseState(initialResState);
        setLoading(false);
    }

    const handleSorting = (event: any) => {
        const req = {
            ...requestState,
            orderBy: event.orderBy,
            order: event.order,
        }
        setRequestState(req);
        (async () => {
            await handleListMemberReservationHistory(req);
        })();
    };

    const handlePagination = (event: any) => {
        const req = {
            ...requestState,
            pageSize: event.pageSize,
            pageNumber: event.pageNumber,
        }
        setRequestState(req);
        (async () => {
            await handleListMemberReservationHistory(req);
        })();
    };

    const CancelDialog = (props: PropsWithChildren<any>) => {
        const [loadingAction, setLoadingAction] = useState(false);

        const handleApproveAction = () => {
            setLoadingAction(true);
            props.onApproveClick();
        };

        const handleClose = () => {
            setShowCancelDialog(false);
        };

        return (
            <Dialog
                open={props.show}
                onClose={(event: any, reason: string) => {
                    if (reason === 'backdropClick' && !loadingAction) {
                        handleClose();
                    }
                }}>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {props.reservationId} nolu rezervasyonu iptal etmek istediğinize emin misiniz?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <CustomButton
                        text="Rezervasyonu İptal Et"
                        className='btn-red'
                        loading={loadingAction}
                        disabled={loadingAction}
                        onClick={handleApproveAction}
                    />
                    <CustomButton
                        text="Vazgeç"
                        className='btn-secondary'
                        disabled={loadingAction}
                        onClick={handleClose}
                    />
                </DialogActions>
            </Dialog>
        );
    };

    const ActionButtons = (row: ListMvkReservationInfoResponseItem) => {
        return (
            <>
                <CustomIconButton
                    className='mr-2'
                    disabled={IsNullOrZero(row.reservationId) || dayjs().isAfter(dayjs(row.reservationDate, 'DD.MM.YYYY HH:mm'))}
                    icon={<Cancel color="inherit" />}
                    onClick={() => {
                        if (!IsNullOrZero(row.reservationId)) {
                            setSelectedCancelReservationId(row.reservationId);
                            setShowCancelDialog(true);
                        }
                    }} />
            </>
        )
    }

    const headCells: DataTableCol<ListMvkReservationInfoResponseItem>[] = props.isReport ? [
        { id: 'reservationId', label: 'Rezervasyon Numarası' },
        { id: 'firstName', label: 'Üye Adı' },
        { id: 'lastName', label: 'Üye Soyadı' },
        { id: 'company', label: 'Şirket' },
        { id: 'phone', label: 'Telefon' },
        {
            id: 'reservationDate', label: 'Rezervasyon Tarihi',
            renderCell: (rowData, fieldName, value) => dayjs(rowData.reservationDate, 'DD.MM.YYYY HH:mm').format('DD.MM.YYYY HH:mm'),
            sortable: true,
        },
    ] : [
        { id: 'reservationId', label: 'Rezervasyon Numarası' },
        {
            id: 'reservationDate', label: 'Rezervasyon Tarihi',
            renderCell: (rowData, fieldName, value) => dayjs(rowData.reservationDate, 'DD.MM.YYYY HH:mm').format('DD.MM.YYYY HH:mm'),
            sortable: true,
        },
        {
            id: 'actions',
            label: 'İşlemler',
            align: 'left',
            width: 60,
            renderCell: (rowData, fieldName, value) => ActionButtons(rowData)
        }
    ];

    return (
        <>
        <Card className={'member-card'} variant="outlined">
            <CardContent>

                {loading ? (
                    <Box pt={1} width="90%">
                        <Skeleton animation="wave" height={60} />
                        <Skeleton animation="wave" height={60} />
                        <Skeleton animation="wave" height={60} />
                        <Skeleton animation="wave" height={60} />
                    </Box>
                ) : null
                }

                {errorText !== '' &&
                    <Box py={1} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <Typography variant="subtitle1" display="block">{errorText}</Typography>
                    </Box>}

                {responseState.totalCount > 0 &&
                    <Box px={2} py={2} width="100%">
                        <DataTable
                            data={responseState}
                            cells={headCells}
                            orderByOption={'reservationDate'}
                            orderOption={'desc'}
                            sortEvent={handleSorting}
                            paginationEvent={handlePagination}
                            pageNumber={requestState.pageNumber}
                            pageSizeOptions={[20, 30]}
                        />
                    </Box>}
            </CardContent>
        </Card>
        {!props.isReport &&
            <CancelDialog show={showCancelDialog} reservationId={selectedCancelReservationId} onApproveClick={() => handleCancelReservation(selectedCancelReservationId)} />
        }
        </>
    );
}

export default ListMvkReservationComponent;